import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { FileUp, Play, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";

import DashboardHeader from "../components/DashboardHeader";

const api = process.env.REACT_APP_BACKEND_URL;
const storageApi = `${api}/api/catalog/storage`;
const importApi = `${api}/api/catalog/import-center`;
const config = { withCredentials: true };
const mappingTargets = [
  "card_name",
  "set_name",
  "game",
  "language",
  "collector_number",
  "rarity",
  "release_date",
  "external_id",
  "image_url",
];

export default function ImportCenterPage({ user, onLogout }) {
  const [files, setFiles] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [file, setFile] = useState(null);
  const [selected, setSelected] = useState(null);
  const [mapping, setMapping] = useState({});
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const [stored, history] = await Promise.all([
        axios.get(`${storageApi}/files`, {
          ...config,
          params: { purpose: "dataset" },
        }),
        axios.get(`${importApi}/jobs`, config),
      ]);
      setFiles(stored.data);
      setJobs(history.data);
    } catch {
      toast.error("Couldn’t load Import Center history.");
    }
  }, []);
  useEffect(() => {
    load();
    const timer = setInterval(load, 3000);
    return () => clearInterval(timer);
  }, [load]);
  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const response = await axios.post(`${storageApi}/files`, form, {
        ...config,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSelected(response.data);
      setFile(null);
      toast.success("Dataset stored. Map columns, then start analysis.");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Dataset upload failed.");
    } finally {
      setBusy(false);
    }
  };
  const analyze = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const response = await axios.post(
        `${importApi}/jobs`,
        { file_id: selected.file_id, column_mapping: mapping },
        config,
      );
      toast.success("Analysis job queued. No catalog records will be changed.");
      await load();
      await viewReport(response.data.import_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn’t start analysis.");
    } finally {
      setBusy(false);
    }
  };
  const viewReport = async (importId) => {
    try {
      const response = await axios.get(
        `${importApi}/jobs/${importId}/report`,
        config,
      );
      setReport(response.data);
    } catch {
      toast.message(
        "Analysis is still running. This view refreshes automatically.",
      );
    }
  };
  const cancel = async (importId) => {
    await axios.post(`${importApi}/jobs/${importId}/cancel`, {}, config);
    toast.success("Cancellation requested.");
    load();
  };
  return (
    <div className="import-center-shell" data-testid="import-center-page">
      <DashboardHeader user={user} activePage="Admin" onLogout={onLogout} />
      <main className="import-center-main">
        <header>
          <p className="eyebrow">Admin · Analysis only</p>
          <h1>Catalog Import Center</h1>
          <p>
            Store a dataset, analyze it, map its columns, and review the final
            no-write preview before any future publishing workflow.
          </p>
        </header>
        <nav className="import-steps" data-testid="import-wizard-steps">
          <span>1 Upload</span>
          <span>2 Analyze</span>
          <span>3 Map</span>
          <span>4 Validate</span>
          <span>5 Preview</span>
        </nav>
        <section className="import-upload">
          <h2>Upload dataset</h2>
          <input
            accept=".csv,.xlsx,.json"
            onChange={(event) => setFile(event.target.files?.[0] || null)}
            type="file"
            data-testid="import-file-input"
          />
          <button
            disabled={!file || busy}
            onClick={upload}
            type="button"
            data-testid="import-upload-button"
          >
            <FileUp size={16} /> {busy ? "Storing…" : "Store dataset"}
          </button>
          <p>
            CSV, XLSX, and JSON stay in persistent GridFS storage. Uploading
            never imports catalog data.
          </p>
        </section>
        <section className="import-grid">
          <div>
            <h2>Stored datasets</h2>
            {files.length ? (
              <table data-testid="import-file-list">
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Format</th>
                    <th>Size</th>
                    <th>Uploaded</th>
                    <th>Hash</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {files.map((item) => (
                    <tr key={item.file_id}>
                      <td>{item.filename}</td>
                      <td>{item.filename.split(".").pop()?.toUpperCase()}</td>
                      <td>{Math.ceil(item.size_bytes / 1024)} KB</td>
                      <td>{new Date(item.uploaded_at).toLocaleString()}</td>
                      <td>{item.sha256.slice(0, 12)}…</td>
                      <td>
                        <button
                          onClick={() => setSelected(item)}
                          type="button"
                          data-testid={`select-import-file-${item.file_id}`}
                        >
                          Select
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="workspace-empty">
                No datasets stored yet. Upload a CSV, XLSX, or JSON file to
                begin.
              </p>
            )}
          </div>
          <div>
            <h2>Column mapping</h2>
            <p>
              {selected
                ? `Selected: ${selected.filename}`
                : "Select a stored dataset first."}
            </p>
            <textarea
              value={JSON.stringify(mapping, null, 2)}
              onChange={(event) => {
                try {
                  setMapping(JSON.parse(event.target.value || "{}"));
                } catch {}
              }}
              placeholder='{"Card Name":"card_name","Set":"set_name"}'
              data-testid="import-column-mapping-input"
            />
            <small>Available targets: {mappingTargets.join(", ")}</small>
            <button
              disabled={!selected || busy}
              onClick={analyze}
              type="button"
              data-testid="import-start-analysis-button"
            >
              <Play size={16} /> Start analysis only
            </button>
          </div>
        </section>
        <section>
          <h2>Import history</h2>
          {jobs.length ? (
            <table data-testid="import-job-history">
              <thead>
                <tr>
                  <th>Dataset</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Provider</th>
                  <th>Created</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.import_id}>
                    <td>{job.filename}</td>
                    <td>{job.status}</td>
                    <td>{job.progress_percent}%</td>
                    <td>{job.provider_id}</td>
                    <td>{new Date(job.created_at).toLocaleString()}</td>
                    <td>
                      {job.status === "analyzed" ? (
                        <button
                          onClick={() => viewReport(job.import_id)}
                          type="button"
                          data-testid={`view-import-report-${job.import_id}`}
                        >
                          View analysis
                        </button>
                      ) : (
                        <button
                          onClick={() => cancel(job.import_id)}
                          type="button"
                          data-testid={`cancel-import-job-${job.import_id}`}
                        >
                          <X size={14} /> Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="workspace-empty">No analysis jobs yet.</p>
          )}
        </section>
        {report && (
          <section
            className="import-report"
            data-testid="import-analysis-report"
          >
            <button onClick={() => setReport(null)} type="button">
              Close
            </button>
            <h2>Dataset analysis</h2>
            <div className="import-report-metrics">
              <span>Rows {report.analysis.rows_detected}</span>
              <span>Ready {report.validation.ready}</span>
              <span>Warnings {report.validation.warning}</span>
              <span>Errors {report.validation.error}</span>
              <span>Duplicate rows {report.analysis.duplicate_rows}</span>
            </div>
            <h3>Columns</h3>
            <p>{report.analysis.columns.join(", ")}</p>
            <h3>Sample rows</h3>
            <pre>{JSON.stringify(report.analysis.sample_rows, null, 2)}</pre>
            <h3>Missing values</h3>
            <pre>{JSON.stringify(report.analysis.missing_values, null, 2)}</pre>
            <h3>Validation results</h3>
            <pre>{JSON.stringify(report.analysis.malformed_rows, null, 2)}</pre>
            <h3>Relationship & import preview</h3>
            <pre>
              {JSON.stringify(
                {
                  relationships: report.relationship_preview,
                  preview: report.import_preview,
                  normalization: report.normalization_preview,
                },
                null,
                2,
              )}
            </pre>
            <button
              onClick={() => {
                const blob = new Blob([JSON.stringify(report, null, 2)], {
                  type: "application/json",
                });
                const url = URL.createObjectURL(blob);
                const anchor = document.createElement("a");
                anchor.href = url;
                anchor.download = `${report.import_id}-validation-report.json`;
                anchor.click();
                URL.revokeObjectURL(url);
              }}
              type="button"
              data-testid="download-validation-report-button"
            >
              <RefreshCw size={15} /> Download validation report
            </button>
            <p className="import-analysis-boundary">
              Analysis only: this preview has not created, updated, merged, or
              published catalog records.
            </p>
          </section>
        )}
      </main>
    </div>
  );
}
